// The bar above every game: the same in each game, so the way back to the room, the rules and who is
// connected are always in one place. "1001" leaves the room (asks first); "End game" (host only, not in
// league games) ends it for everyone (asks first); "Back to room" once it's over.
import React from 'react';
import type { ParticipantView, RoomView } from '../../types/room';
import { leagueOfRoom } from './leagueRoom';


export default function InGameBar({ room, myId, isLeader, gameName, seatName, onHome, onBackToRoom }: {
  room: RoomView; myId: string; isLeader: boolean; gameName: string;
  seatName: (slot: number, p: ParticipantView) => string;
  onHome: () => void; onBackToRoom: () => void;
}) {
  const [copied, setCopied] = React.useState(false);
  const players = room.participants.filter(p => p.slot !== null).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  const watching = room.participants.filter(p => p.slot === null);
  const invite = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/room/${room.slug}`).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  const DOTS = ['#8fb8dd', '#ec9a85', '#9fd3a6', '#c7a6e8', '#f2c46b', '#7fd1c7', '#e8a0c8', '#b8c47a'];
  return (
    <header className="site-header" style={{ padding: '8px 20px', gap: '8px 14px' }}>
      <button type="button" className="site-brand" style={{ fontSize: 20, paddingRight: 14, borderRight: '1px solid var(--panel-line)' }} title="Leave the room (asks first)" onClick={onHome}>1001</button>
      <span className="site-display" style={{ fontSize: 24, textTransform: 'none', letterSpacing: '.03em' }}>{gameName}</span>
      <ul aria-label="Players" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8, flex: '1 1 auto' }}>
        {players.map((p, i) => {
          const away = !p.isConnected && p.type !== 'bot';
          const side = seatName(p.slot ?? i, p);
          const c = side === 'Blue' ? '#8fb8dd' : side === 'Red' ? '#ec9a85' : DOTS[i % DOTS.length];
          return (
            <li key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, borderRadius: 999, padding: '3px 12px 3px 3px', fontSize: 14,
              border: `1px ${away ? 'dashed' : 'solid'} ${c}`, background: 'var(--bg)' }}>
              <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: '50%', background: away ? 'transparent' : c, border: away ? `1px solid ${c}` : 'none', color: away ? c : '#1d2733', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.name[0]?.toUpperCase()}</span>
              {p.name}{p.id === myId ? ' (you)' : ''} · {side}{p.isLeader && !leagueOfRoom(room) ? ' · host' : ''}{p.type === 'bot' ? ' · bot' : ''}
              {away && <span style={{ color: 'var(--danger)' }}> · reconnecting</span>}
            </li>
          );
        })}
        {watching.length > 0 && (
          <li style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--panel-line)', borderRadius: 999, padding: '3px 12px', fontSize: 14, color: 'var(--muted)' }}>
            {watching.map(w => w.name + (w.id === myId ? ' (you)' : '') + (w.isLeader && !leagueOfRoom(room) ? ' · host' : '')).join(', ')} watching
          </li>
        )}
      </ul>
      <a className="site-btn" href={`/games/${room.gameId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ink)' }}>Rules</a>
      <button type="button" className="site-btn" title="Copy the room link" onClick={invite}>{copied ? 'Copied!' : 'Invite'}</button>
      {/* Over: everyone can go back. While it's on, only the host can end it early (not in a league game,
          which ends when it's won or resigned), and the button says that's what it does. */}
      {room.result
        ? <button type="button" className="site-btn site-btn-primary" title="The game is over: back to the room for everyone" onClick={onBackToRoom}>Back to room</button>
        : isLeader && !leagueOfRoom(room) && <button type="button" className="site-btn" title="End this game for everyone and go back to the room (asks first)" onClick={onBackToRoom}>End game</button>}
    </header>
  );
}
